import React, { useEffect, useMemo } from 'react';

import type { LinkElement } from '@aline/common';
import { linkType } from '@aline/common';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import type { Range } from 'slate';
import { Editor, Element } from 'slate';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useFocused } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import { LinkForm, LinkFormType } from './LinkForm';

interface InsertLinkState {
  insertLinkRange: Range;
  selectedText: string;
}
export const insertLinkPopoverVar = makeVar<InsertLinkState | null>(null);

export function useInsertLink(): InsertLinkState | null {
  return useReactiveVar(insertLinkPopoverVar);
}

export function InsertLinkForm({
  insertLinkRange,
  selectedText,
}: InsertLinkState): JSX.Element | null {
  const editor = useEditor();
  const isFocused = useFocused();
  const currentUserId = useCurrentUserId();
  const { commenting, viewing } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);

  // If there is a link somewhere in the selection, we set the initial URL of the form to the href from that link node.
  const initialHref = useMemo(() => {
    const linkEntries = Editor.nodes<LinkElement>(editor, {
      at: insertLinkRange,
      match: node => Element.isElement(node) && node.type === linkType,
    });

    const firstLinkEntry = linkEntries.next().value;
    if (firstLinkEntry) {
      const [firstLink] = firstLinkEntry;
      return firstLink.href ?? '';
    }

    return '';
  }, [editor, insertLinkRange]);

  useEffect(() => {
    if (isFocused) insertLinkPopoverVar(null);
  }, [isFocused]);

  function onSubmit(href: string, options: { modifiedLinkText?: string } = {}): void {
    const { modifiedLinkText } = options;

    if (!currentUserId || !editingMode) return;

    if (href.length > 0 && insertLinkRange) {
      modifiedLinkText
        ? AuthorizedTransforms.insertLink(editor, {
            at: insertLinkRange,
            authorId: currentUserId,
            editingMode,
            newLinkProps: { href, modifiedLinkText },
          })
        : AuthorizedTransforms.insertLink(editor, {
            at: insertLinkRange,
            authorId: currentUserId,
            editingMode,
            newLinkProps: { href },
          });
    }
  }

  if (viewing || commenting) return null;

  return (
    <LinkForm
      afterSubmit={() => insertLinkPopoverVar(null)}
      closeLinkEditor={() => insertLinkPopoverVar(null)}
      initialHref={initialHref}
      initialText={selectedText}
      type={LinkFormType.INSERT_LINK}
      onSubmit={onSubmit}
    />
  );
}
